import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Product-page hat upsell. Design changes arrive as productSelect section HTML.
 * Style and size stay on this element and are restored after that swap.
 *
 * @typedef {object} MatchingHatUpsellRefs
 * @property {HTMLElement} panel
 * @property {HTMLButtonElement} [addButton]
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<MatchingHatUpsellRefs>}
 */
class MatchingHatUpsell extends Component {
  /** @type {boolean} */
  #adding = false;

  /** @type {number | undefined} */
  #addedTimeout;

  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleClick);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleClick);
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
  }

  /**
   * @param {Event} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const productEvent = /** @type {import('@shopify/events').ProductSelectEvent} */ (event);
    const style = this.#checkedStyle();
    const size = this.#activeGroup()?.querySelector('[data-size-input]:checked');
    const selectedStyle = style?.value ?? '';
    const selectedSize = size instanceof HTMLInputElement ? size.value : '';

    productEvent.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html || typeof html.querySelector !== 'function') return;

        const next = html.querySelector(`matching-hat-upsell[data-block-id="${this.dataset.blockId}"]`);
        if (!(next instanceof HTMLElement)) {
          this.hidden = true;
          return;
        }

        const nextPanel = next.querySelector('[ref="panel"]');
        const panel = this.querySelector('[ref="panel"]');
        if (nextPanel && panel) panel.replaceWith(nextPanel);

        this.hidden = next.hasAttribute('hidden');
        if (detail.productId) this.dataset.productId = detail.productId;
        this.#restore(selectedStyle, selectedSize);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[matching-hat-upsell] Variant update failed:', error);
      });
  };

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.hasAttribute('data-style-input')) this.#showActiveStyle();
    if (target.hasAttribute('data-style-input') || target.hasAttribute('data-size-input')) {
      this.#clearError();
      this.#sync();
    }
  };

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[ref="addButton"]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;
    event.preventDefault();
    this.#add(button);
  };

  /**
   * @param {string} styleId
   * @param {string} sizeValue
   */
  #restore(styleId, sizeValue) {
    if (styleId) {
      const styleInput = this.querySelector(`[data-style-input][value="${styleId}"]`);
      if (styleInput instanceof HTMLInputElement) styleInput.checked = true;
    }

    this.#showActiveStyle();

    const group = this.#activeGroup();
    if (group && sizeValue) {
      const sizeInput = Array.from(group.querySelectorAll('[data-size-input]')).find(
        (input) => input instanceof HTMLInputElement && input.value === sizeValue && !input.disabled
      );
      if (sizeInput instanceof HTMLInputElement) sizeInput.checked = true;
    }

    this.#sync();
  }

  #showActiveStyle() {
    const styleId = this.#checkedStyle()?.value;
    for (const group of this.querySelectorAll('[data-size-group]')) {
      if (!(group instanceof HTMLElement)) continue;
      group.hidden = group.dataset.productId !== styleId;
    }
  }

  #sync() {
    const group = this.#activeGroup();
    const selected = group?.querySelector('[data-size-input]:checked');
    const variantId = selected instanceof HTMLInputElement ? selected.dataset.variantId : '';

    for (const price of this.querySelectorAll('[data-price-for]')) {
      if (!(price instanceof HTMLElement)) continue;
      const inGroup = group?.contains(price) ?? false;
      price.hidden = !(inGroup && variantId && price.dataset.priceFor === variantId);
    }

    if (group && !variantId) {
      const fallback = group.querySelector('[data-price-for]');
      if (fallback instanceof HTMLElement) fallback.hidden = false;
    }

    const button = this.querySelector('[ref="addButton"]');
    if (button instanceof HTMLButtonElement) {
      const unavailable = !(selected instanceof HTMLInputElement) || selected.disabled || !variantId;
      button.disabled = unavailable || this.#adding;
      button.dataset.variantId = variantId || '';
    }

    const imageSrc = selected instanceof HTMLInputElement ? selected.dataset.imageSrc : '';
    const image = styleIdImage(this, this.#checkedStyle()?.value);
    if (image && imageSrc) {
      image.src = imageSrc;
      image.removeAttribute('srcset');
    }
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #add(button) {
    if (this.#adding || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#adding = true;
    this.#clearError();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      }).then((result) => result.json());

      if (response.status) {
        const message = response.description || response.message || 'Could not add this hat';
        this.#showError(message);
        const ajaxCart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            items: ajaxCart.items,
            itemCount: 1,
            productId: this.dataset.productId,
          },
        });
        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        return;
      }

      const ajaxCart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          sections: response.sections,
          itemCount: 1,
          productId: this.dataset.productId,
        },
      });

      const added = Theme.translations?.added || 'Added';
      const status = this.querySelector('[ref="status"]');
      if (status) status.textContent = added;
      const label = button.dataset.label || button.textContent || '';
      button.textContent = added;
      if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
      this.#addedTimeout = window.setTimeout(() => {
        button.textContent = label;
        if (status) status.textContent = '';
      }, 1600);
    } catch (error) {
      console.error(error);
      const message = 'Could not add this hat';
      this.#showError(message);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#adding = false;
      button.removeAttribute('aria-busy');
      this.#sync();
    }
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const error = this.querySelector('[ref="error"]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = false;
  }

  #clearError() {
    const error = this.querySelector('[ref="error"]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = '';
    error.hidden = true;
  }

  /** @returns {HTMLInputElement | null} */
  #checkedStyle() {
    const style = this.querySelector('[data-style-input]:checked');
    return style instanceof HTMLInputElement ? style : null;
  }

  /** @returns {HTMLElement | null} */
  #activeGroup() {
    const styleId = this.#checkedStyle()?.value;
    if (!styleId) return null;
    const group = this.querySelector(`[data-size-group][data-product-id="${styleId}"]`);
    return group instanceof HTMLElement ? group : null;
  }
}

/**
 * @param {ParentNode} root
 * @param {string | undefined} productId
 * @returns {HTMLImageElement | null}
 */
function styleIdImage(root, productId) {
  if (!productId) return null;
  const image = root.querySelector(`[data-style-image="${productId}"]`);
  return image instanceof HTMLImageElement ? image : null;
}

if (!customElements.get('matching-hat-upsell')) {
  customElements.define('matching-hat-upsell', MatchingHatUpsell);
}
